import { ApolloError } from 'apollo-boost';
import { FormikErrors } from 'formik';
import _get from 'lodash/get';
import _includes from 'lodash/includes';
import { CF_RAY_ID_REGEXP, NETWORK_ERROR_CODE } from '../constants';
import copy from '../copy';

// cache the session data locally and check for localStorage on window
// as users can disable it, and we dont want this to prevent logging in

let sessionData: string | boolean | null;
const LS_KEY = 'login_session_data';
const VERIFIED_INLINE_ERROR_SHAPES = [/You have reached your Rate Limit/i];

// Adapted from https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#Feature-detecting_localStorage
function isStorageAvailable(): boolean {
  let storage;
  try {
    storage = window.localStorage;
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return !!(
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    );
  }
}

export const addSessionData = (values: object = {}): any => {
  if (!sessionData) {
    sessionData = isStorageAvailable() && window.localStorage.getItem(LS_KEY);
  }
  return sessionData ? { ...values, session_data: sessionData } : values;
};

export const saveSessionData = (newSessionData?: string): void => {
  if (newSessionData) {
    sessionData = newSessionData;
    if (isStorageAvailable()) {
      window.localStorage.setItem(LS_KEY, sessionData);
    }
  }
};

export const getSessionData = (sessionItem: string): string => {
  return (
    (isStorageAvailable() && window.localStorage.getItem(sessionItem)) || ''
  );
};

/**
 * The graphql server returns an error with a shape
 *
 *   { code: 'email_exists', field: 'email' }
 *
 * We want to pluck out this error, and transform 'code' into a string
 */

const processError = (error: ApolloError) => {
  const errorInResponse = _get(error, 'graphQLErrors[0]');
  const networkError = _get(error, 'networkError');

  if (
    networkError &&
    _get(networkError, 'statusCode') === NETWORK_ERROR_CODE &&
    _includes(
      _get(networkError, 'bodyText', ''),
      'Sorry, you have been blocked'
    )
  ) {
    const rayIdMatches = new RegExp(CF_RAY_ID_REGEXP, 'gi').exec(
      _get(networkError, 'bodyText', '')
    );
    let rayId: string = '';

    if (rayIdMatches) {
      rayId = rayIdMatches.length > 3 ? rayIdMatches[3] : rayIdMatches[2];
    }

    return {
      message: {
        title: copy.errors.request_blocked.title,
        details: `${copy.errors.request_blocked.details}${
          rayId ? ` and reference Ray ID: ${rayId.trim()}.` : '.'
        }`
      },
      field: null
    };
  } else if (!errorInResponse) {
    // if theres an verified inline message (rate limit etc) use that
    const message = _get(error, 'message');
    if (
      message &&
      VERIFIED_INLINE_ERROR_SHAPES.find((messageRegex) =>
        message.match(messageRegex)
      )
    ) {
      return {
        message: {
          title: copy.error_with_message_title,
          details: _get(error, 'message')
        }
      };
    }
    return {
      message: copy.errors.unknown,
      field: null
    };
  }

  const errorCode = _get(errorInResponse, 'message');
  const errorMessage = copy.errors[errorCode] || copy.errors.unknown;

  return {
    message: errorMessage,
    field: _get(errorInResponse, 'field')
  };
};

export const handleFormError = (
  setErrors: (errs: FormikErrors<object>) => void,
  setGenericServerError: (err: any) => void
) => {
  return (errorResponse: ApolloError) => {
    const { message, field } = processError(errorResponse);
    if (field) {
      return setErrors({ [field]: message.details });
    }
    setGenericServerError(message);
  };
};
